import { useState, useEffect, useCallback } from 'react';
import { getApiKey } from '../../../utils/storage';
import {
  getOrganizationUsageDetails,
  UsageDetailsData,
  ApiKeyUsageDetail,
  ApiResponse,
} from '../../../utils/openai-api';
import { getDateRangeFilter } from '../../../utils/dateUtils';
import { DateRange } from '../../../constants/ui';
import { EmployeeRecord } from '@/utils/employee-indexeddb';
import CurrencyManager from '@/utils/currencyManager';
import {
  generateHeadquarterHtmlReportWithUsageData,
  generateHeadquarterHtmlReportWithComparison,
  downloadHtmlReport,
  ApiKeyUsageData
} from '@/utils/html-report-generator';
import { getAnthropicUsageDetails } from '@/utils/anthropic-usage-adapter';
import { mergeUsageDetailsData } from '@/utils/usage-merge';
import { UsageDataSource, ExportRange } from '../types';
import { buildIdentifierVariants } from '../utils/organization';
import { FilteredDataResult } from './useFilteredData';
import { debugLog } from '../../../utils/debug-log';

export interface UseExportReportParams {
  data: UsageDetailsData | null;
  filteredData: FilteredDataResult | null;
  employeeData: Map<string, EmployeeRecord>;
  findEmployeeForApiKey: (apiKey: ApiKeyUsageDetail) => EmployeeRecord | undefined;
  getDateRange: () => { startDate: Date; endDate: Date; label: string };
  dataSource: UsageDataSource;
  resolvedHasAnthropicKey: boolean;
  loading: boolean;
  customDateRange: { startDate: Date; endDate: Date } | null;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  setCustomDateRange: (range: { startDate: Date; endDate: Date } | null) => void;
}

export interface UseExportReportReturn {
  pendingExportRange: ExportRange | null;
  handleExportHtmlReport: (range: ExportRange) => void;
}

export function useExportReport({
  data,
  filteredData,
  employeeData,
  findEmployeeForApiKey,
  dataSource,
  resolvedHasAnthropicKey,
  loading,
  customDateRange,
  dateRange,
  setDateRange,
  setCustomDateRange,
}: UseExportReportParams): UseExportReportReturn {
  const [pendingExportRange, setPendingExportRange] = useState<ExportRange | null>(null);

  const performExportHtmlReport = useCallback(async (exportRange: ExportRange) => {
    const usageDetails = filteredData?.apiKeyUsage ?? data?.apiKeyUsage;

    if (!usageDetails || usageDetails.length === 0) {
      console.error('No usage data available for export');
      alert('No usage data available for export. Please load data first.');
      return;
    }

    try {
      const allEmployees = Array.from(employeeData.values());
      const employeesByEmail = new Map<string, EmployeeRecord>();
      allEmployees.forEach(emp => {
        const existing = employeesByEmail.get(emp.email);
        if (!existing || (emp.lastUpdated && (!existing.lastUpdated || emp.lastUpdated > existing.lastUpdated))) {
          employeesByEmail.set(emp.email, emp);
        }
      });
      const uniqueEmployees = Array.from(employeesByEmail.values());

      const buildReportUsageData = (apiKey: ApiKeyUsageDetail): ApiKeyUsageData => {
        const aliasSet = new Set<string>();
        buildIdentifierVariants(apiKey.id).forEach(value => aliasSet.add(value));
        if (Array.isArray(apiKey.sourceIds)) {
          apiKey.sourceIds.forEach(sourceId => {
            buildIdentifierVariants(sourceId).forEach(value => aliasSet.add(value));
          });
        }
        aliasSet.delete(apiKey.id);
        return {
          id: apiKey.id,
          name: apiKey.name,
          requests: apiKey.numberOfRequests ?? apiKey.requests ?? 0,
          cost: apiKey.totalCost ?? apiKey.cost ?? 0,
          aliases: Array.from(aliasSet)
        };
      };

      const enrichedEmployees: EmployeeRecord[] = uniqueEmployees.map(emp => {
        let apiKeyId = emp.apiKeyId;
        let apiKeyName = emp.apiKeyName;

        for (const apiKey of usageDetails) {
          const employee = findEmployeeForApiKey(apiKey);
          if (employee && employee.email === emp.email) {
            apiKeyId = apiKey.id;
            apiKeyName = apiKey.name;
            break;
          }
        }

        return { ...emp, apiKeyId, apiKeyName };
      });

      const currentPeriodUsageData: ApiKeyUsageData[] = usageDetails.map(buildReportUsageData);

      const currentApiKeySummary = {
        totalApiKeys: currentPeriodUsageData.length,
        activeApiKeys: currentPeriodUsageData.filter(key => (key.requests ?? 0) > 0).length
      };

      const dateRangeLabels: Record<ExportRange, string> = {
        '1day': '\u672C\u65E5',
        '7days': '\u904E\u53BB7\u65E5',
        '30days': '\u904E\u53BB30\u65E5'
      };

      const currentPeriodLabel = dateRangeLabels[exportRange] || '\u672C\u65E5';

      const currentRangeInfo = filteredData?.dateRangeInfo;
      const currentRange = currentRangeInfo
        ? {
            startDate: new Date(currentRangeInfo.startDateIso),
            endDate: new Date(currentRangeInfo.endDateIso)
          }
        : (() => {
            const { startDate, endDate } = getDateRangeFilter(exportRange as DateRange);
            return { startDate, endDate };
          })();

      const currentDuration = Math.max(
        0,
        currentRange.endDate.getTime() - currentRange.startDate.getTime()
      );
      const prevEndDate = new Date(currentRange.startDate.getTime() - 1);
      const prevStartDate = new Date(prevEndDate.getTime() - currentDuration);

      let previousPeriodUsageData: ApiKeyUsageData[] = [];
      const previousUsageDetails: UsageDetailsData[] = [];

      const collectPreviousUsage = (result: ApiResponse<UsageDetailsData>, _providerLabel: string) => {
        if (result.success && result.data?.apiKeyUsage) {
          previousUsageDetails.push(result.data);
        }
      };

      if ((dataSource === 'anthropic' || dataSource === 'all') && resolvedHasAnthropicKey) {
        try {
          const result = await getAnthropicUsageDetails(
            { startDate: prevStartDate, endDate: prevEndDate },
            (message) => debugLog('Fetching previous period (Anthropic):', message)
          );
          collectPreviousUsage(result, 'Anthropic');
        } catch (error) {
          console.warn('Failed to fetch Anthropic previous period data:', error);
        }
      }

      if (dataSource === 'openai' || dataSource === 'all') {
        const apiKey = getApiKey();
        if (apiKey) {
          try {
            const result = await getOrganizationUsageDetails(
              apiKey,
              (message) => debugLog('Fetching previous period (OpenAI):', message),
              { startDate: prevStartDate, endDate: prevEndDate },
              false
            );
            collectPreviousUsage(result, 'OpenAI');
          } catch (error) {
            console.warn('Failed to fetch OpenAI previous period data:', error);
          }
        }
      }

      let prevRawApiKeyUsage: ApiKeyUsageDetail[] = [];
      if (previousUsageDetails.length === 1) {
        prevRawApiKeyUsage = previousUsageDetails[0].apiKeyUsage;
      } else if (previousUsageDetails.length > 1) {
        prevRawApiKeyUsage = mergeUsageDetailsData(previousUsageDetails).apiKeyUsage;
      }
      previousPeriodUsageData = prevRawApiKeyUsage.map(buildReportUsageData);

      const enrichedEmployeesPrevious: EmployeeRecord[] = uniqueEmployees.map(emp => {
        let apiKeyId = emp.apiKeyId;
        let apiKeyName = emp.apiKeyName;

        for (const apiKey of prevRawApiKeyUsage) {
          const employee = findEmployeeForApiKey(apiKey);
          if (employee && employee.email === emp.email) {
            apiKeyId = apiKey.id;
            apiKeyName = apiKey.name;
            break;
          }
        }

        return { ...emp, apiKeyId, apiKeyName };
      });

      const previousApiKeySummary = {
        totalApiKeys: previousPeriodUsageData.length,
        activeApiKeys: previousPeriodUsageData.filter(key => (key.requests ?? 0) > 0).length
      };

      let htmlContent: string;
      const now = new Date();
      const yearMonthDay = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const fileName = `report-${yearMonthDay}.html`;

      const exchangeRate = CurrencyManager.getInstance().getExchangeRate();

      if (previousPeriodUsageData.length > 0) {
        htmlContent = generateHeadquarterHtmlReportWithComparison(
          enrichedEmployees,
          currentPeriodUsageData,
          previousPeriodUsageData,
          'API\u5229\u7528\u72B6\u6CC1\u5831\u544A',
          currentPeriodLabel,
          '\u4EE5\u524D',
          { start: currentRange.startDate, end: currentRange.endDate },
          { start: prevStartDate, end: prevEndDate },
          {
            current: currentApiKeySummary,
            previous: previousApiKeySummary
          },
          exchangeRate,
          enrichedEmployeesPrevious
        );
      } else {
        htmlContent = generateHeadquarterHtmlReportWithUsageData(
          enrichedEmployees,
          currentPeriodUsageData,
          'API\u5229\u7528\u72B6\u6CC1\u5831\u544A',
          currentPeriodLabel,
          currentApiKeySummary,
          exchangeRate
        );
      }

      downloadHtmlReport(htmlContent, fileName);
    } catch (error) {
      console.error('Failed to generate HTML report:', error);
    }
  }, [data, filteredData, employeeData, findEmployeeForApiKey, dataSource, resolvedHasAnthropicKey]);

  const handleExportHtmlReport = useCallback((range: ExportRange) => {
    if (!loading && !customDateRange && dateRange === range) {
      performExportHtmlReport(range);
      return;
    }
    setPendingExportRange(range);
    setCustomDateRange(null);
    setDateRange(range);
  }, [loading, customDateRange, dateRange, performExportHtmlReport, setCustomDateRange, setDateRange]);

  useEffect(() => {
    if (!pendingExportRange) {
      return;
    }
    if (loading) {
      return;
    }
    if (customDateRange) {
      return;
    }
    if (dateRange !== pendingExportRange) {
      return;
    }
    performExportHtmlReport(pendingExportRange)
      .finally(() => setPendingExportRange(null));
  }, [pendingExportRange, loading, customDateRange, dateRange, performExportHtmlReport]);

  return {
    pendingExportRange,
    handleExportHtmlReport,
  };
}
